import { computed, nextTick, reactive, ref, watch } from "vue";

export default function useNovoContatoDialog(props, emit) {
  const dialogVisible = ref(false);
  const formRef = ref(null);
  const isSaving = ref(false);
  const duplicateInfo = ref(null);
  const emailDuplicado = ref("");
  const telefoneDuplicado = ref("");
  const form = reactive({
    nome_representante: "",
    cargo_representante: "",
    telefone: "",
    email: "",
  });
  let lastPayload = null;

  const preencherForm = () => {
    form.nome_representante = "";
    form.cargo_representante = "";
    form.telefone = "";
    form.email = "";
  };

  watch(() => props.visible, (newVal) => {
    dialogVisible.value = newVal;
    if (newVal) {
      preencherForm();
      duplicateInfo.value = null;
      emailDuplicado.value = "";
      telefoneDuplicado.value = "";
      nextTick(() => {
        if (formRef.value) {
          formRef.value.resetValidation();
        }
      });
    }
  });

  const closeDialog = () => {
    dialogVisible.value = false;
    duplicateInfo.value = null;
    emailDuplicado.value = "";
    telefoneDuplicado.value = "";
    if (formRef.value) {
      formRef.value.resetValidation();
    }
    emit("fechar");
  };

  watch(() => form.email, () => {
    if (emailDuplicado.value) {
      emailDuplicado.value = "";
      if (formRef.value) {
        formRef.value.validate();
      }
    }
  });

  watch(() => form.telefone, () => {
    if (telefoneDuplicado.value) {
      telefoneDuplicado.value = "";
      if (formRef.value) {
        formRef.value.validate();
      }
    }
  });

  const formatCNPJ = (cnpj) => {
    if (!cnpj) return "—";
    const numbers = cnpj.toString().replace(/\D/g, "");
    if (numbers.length === 14) {
      return numbers.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
    }
    return cnpj;
  };

  const requiredRule = (value) => {
    if (!value || value.toString().trim() === "") {
      return "Campo obrigatório";
    }
    return true;
  };

  const sanitizeTelefone = (value) => {
    return (value || "").replace(/\D/g, "").slice(0, 11);
  };

  const formatTelefoneDisplay = (digits) => {
    if (!digits) return "";
    if (digits.length <= 2) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const telefoneMasked = computed({
    get: () => formatTelefoneDisplay(form.telefone),
    set: (value) => {
      form.telefone = sanitizeTelefone(value);
    }
  });

  const whatsappRule = () => {
    return form.telefone.length >= 10 || "Formato inválido";
  };

  const emailRule = (value) => {
    if (!value || value.toString().trim() === "") {
      return "Campo obrigatório";
    }
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(value.trim()) || "Formato inválido";
  };

  const emailDuplicadoRule = () => {
    return emailDuplicado.value || true;
  };

  const telefoneDuplicadoRule = () => {
    return telefoneDuplicado.value || true;
  };

  const handleSalvar = () => {
    if (isSaving.value) return;
    emailDuplicado.value = "";
    telefoneDuplicado.value = "";

    if (formRef.value) {
      const valido = formRef.value.validate();
      if (!valido) {
        return;
      }
    }

    const fornecedorId = props.fornecedor && (props.fornecedor.idfornecedor || props.fornecedor.id);

    if (!fornecedorId) {
      return;
    }

    isSaving.value = true;
    duplicateInfo.value = null;
    const telefoneDigitsValue = form.telefone;
    lastPayload = {
      fornecedorId,
      contato: {
        nome_representante: form.nome_representante.trim(),
        cargo_representante: form.cargo_representante.trim(),
        telefone: telefoneDigitsValue,
        email: form.email.trim(),
      },
      options: {},
    };

    emit("salvar", lastPayload, (result) => {
      isSaving.value = false;
      const success = result && result.success;
      const duplicate = result && result.duplicate;

      if (success) {
        duplicateInfo.value = null;
        emailDuplicado.value = "";
        telefoneDuplicado.value = "";
        closeDialog();
        return;
      }
      if (duplicate) {
        duplicateInfo.value = result;
        if (result.campoDuplicado === 'email') {
          emailDuplicado.value = result.message || "E-mail já cadastrado";
          if (formRef.value) {
            formRef.value.validate();
          }
        } else if (result.campoDuplicado === 'telefone') {
          telefoneDuplicado.value = result.message || "Telefone já cadastrado";
          if (formRef.value) {
            formRef.value.validate();
          }
        }
        return;
      }
      if (result && result.errorMessage) {
        duplicateInfo.value = null;
      }
    });
  };

  const limparDuplicado = () => {
    duplicateInfo.value = null;
  };

  const criarComoAlternativo = () => {
    if (!lastPayload) return;
    duplicateInfo.value = null;
    isSaving.value = true;
    const payload = {
      ...lastPayload,
      options: { alternativo: true },
    };

    emit("salvar", payload, (result) => {
      isSaving.value = false;
      const success = result && result.success;
      const duplicate = result && result.duplicate;

      if (success) {
        duplicateInfo.value = null;
        closeDialog();
      } else if (duplicate) {
        duplicateInfo.value = result;
      }
    });
  };

  const emitAtualizarExistente = () => {
    if (duplicateInfo.value && duplicateInfo.value.contatoExistente) {
      emit("editar-existente", duplicateInfo.value.contatoExistente);
      duplicateInfo.value = null;
      dialogVisible.value = false;
    }
  };

  return {
    dialogVisible,
    formRef,
    isSaving,
    duplicateInfo,
    form,
    telefoneMasked,
    formatCNPJ,
    requiredRule,
    whatsappRule,
    emailRule,
    emailDuplicadoRule,
    telefoneDuplicadoRule,
    closeDialog,
    handleSalvar,
    limparDuplicado,
    criarComoAlternativo,
    emitAtualizarExistente,
  };
}
